// src/pages/Student/StudentSettings.js

import React, { useState, useEffect } from 'react';
import { auth, db } from '../../firebase';
import {
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import {
  doc,
  deleteDoc,
  writeBatch,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import Preloader from '../../components/Preloader';

const StudentSettings = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if user is signed in
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        console.log('No user is signed in.');
        window.location.href = '/sign-in'; // Redirect if not authenticated
      }
    });

    return () => unsubscribeAuth();
  }, []);

  const handleResetPassword = async () => {
    setLoading(true);
    try {
      if (user && user.email) {
        await sendPasswordResetEmail(auth, user.email);
        alert('Password reset email sent. Please check your inbox.');
      } else {
        alert('No authenticated user found.');
      }
    } catch (error) {
      console.error('Error sending password reset email:', error);
      alert('Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete your account? This action cannot be undone.'
      )
    ) {
      const password = prompt('Please enter your password to confirm account deletion:');
      if (!password) {
        alert('Password is required to delete account.');
        return;
      }

      setLoading(true);
      try {
        if (user && user.email) {
          // Reauthenticate user
          await signInWithEmailAndPassword(auth, user.email, password);

          // Delete related data: enrollments, grades
          const batch = writeBatch(db);
          const collectionsToDelete = ['enrollments', 'grades'];
          for (const collectionName of collectionsToDelete) {
            const q = query(
              collection(db, collectionName),
              where('student_uid', '==', user.uid)
            );
            const querySnapshot = await getDocs(q);
            querySnapshot.forEach((docSnap) => {
              batch.delete(doc(db, collectionName, docSnap.id));
            });
          }

          await batch.commit();

          // Delete user settings
          await deleteDoc(doc(db, 'users', user.uid));

          // Delete user from Firebase Auth
          await user.delete();

          alert('Account deleted successfully.');
          window.location.href = '/sign-up';
        } else {
          alert('No authenticated user found.');
        }
      } catch (error) {
        console.error('Error deleting account:', error);
        alert('Failed to delete account. Please ensure your password is correct and try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSignOut = () => {
    signOut(auth)
      .then(() => {
        window.location.href = '/sign-in';
      })
      .catch((error) => {
        console.error('Error signing out:', error);
      });
  };

  return (
    <>
      {/* Main container with theme variables and transitions */}
      <main className="bg-[var(--bg-color)] text-[var(--text-color)] transition-all">
        <section className="mx-auto max-w-3xl px-0 py-0">
          {loading && <Preloader />}

          <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
            <h3 className="mb-3 text-lg font-semibold">Account Security</h3>
            <button
              onClick={handleResetPassword}
              disabled={loading}
              className="w-full px-4 py-2 rounded-md font-semibold
                         bg-[var(--primary-color)] text-white
                         hover:bg-[var(--hover-color)] disabled:opacity-60
                         transition-colors"
            >
              Reset Password
            </button>
          </div>

          <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
            <h3 className="mb-3 text-lg font-semibold">Account Management</h3>
            <button
              onClick={handleDeleteAccount}
              disabled={loading}
              className="w-full px-4 py-2 rounded-md font-semibold
                         bg-[var(--primary-color)] text-white
                         hover:bg-[var(--hover-color)] disabled:opacity-60
                         transition-colors"
            >
              Delete Account
            </button>
          </div>

          <div className="rounded-lg border p-4 sm:col-span-2" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
            <button
              onClick={handleSignOut}
              disabled={loading}
              className="w-full px-4 py-2 rounded-md font-semibold
                         bg-[var(--primary-color)] text-white
                         hover:bg-[var(--hover-color)] disabled:opacity-60
                         transition-colors"
            >
              Sign Out
            </button>
          </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default StudentSettings;
